import React, { useRef, useState } from 'react';
import { FileText, Loader2, Upload } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { pdfUploadCopy } from '../locales/pdfUpload';

interface Props {
  inputLabel: string;
  hint: string;
  busy: boolean;
  onFile: (file: File) => void;
}

export default function CompanyPdfUpload({ inputLabel, hint, busy, onFile }: Props) {
  const { language, t } = useLanguage();
  const copy = pdfUploadCopy[language];
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <div
      aria-busy={busy}
      onDragOver={event => { event.preventDefault(); if (!busy) setDragging(true); }}
      onDragLeave={event => { if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
      onDrop={event => {
        event.preventDefault(); setDragging(false);
        const file = event.dataTransfer.files[0];
        if (file && !busy) onFile(file);
      }}
      className={`rounded-2xl border-2 p-4 sm:p-5 transition-colors ${dragging ? 'border-goszakup-600 bg-goszakup-100' : 'border-sky-200 bg-goszakup-50'}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-goszakup-600 text-white">
          <FileText aria-hidden="true" className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold text-slate-900">{copy.title}</p>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-700">{copy.description}</p>
          {busy ? (
            <p role="status" className="mt-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Loader2 aria-hidden="true" className="h-5 w-5 shrink-0 animate-spin" />{t('pdfImport.processing')}
            </p>
          ) : (
            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
              <button type="button" onClick={() => inputRef.current?.click()}
                className="inline-flex min-h-11 max-w-full items-center justify-center gap-2 rounded-xl bg-goszakup-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-goszakup-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goszakup-600 focus-visible:ring-offset-2">
                <Upload aria-hidden="true" className="h-4 w-4 shrink-0" />{copy.choose}
              </button>
              <span className="text-xs text-slate-700">{copy.drop}</span>
            </div>
          )}
          <p className="mt-2 text-xs leading-relaxed text-goszakup-700">{hint}</p>
        </div>
      </div>
      <input ref={inputRef} type="file" accept=".pdf,application/pdf" aria-label={inputLabel}
        disabled={busy} className="hidden" onChange={event => {
          const file = event.target.files?.[0];
          if (file && !busy) onFile(file);
          event.target.value = '';
        }} />
    </div>
  );
}
